"use client";

import { useEffect, useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;

type StyleCode = "t" | "T" | "d" | "D" | "f" | "F" | "R";

const ABSOLUTE_STYLE_OPTIONS: Record<
  Exclude<StyleCode, "R">,
  Intl.DateTimeFormatOptions
> = {
  t: { hour: "numeric", minute: "2-digit" },
  T: { hour: "numeric", minute: "2-digit", second: "2-digit" },
  d: { month: "2-digit", day: "2-digit", year: "numeric" },
  D: { month: "long", day: "numeric", year: "numeric" },
  f: {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
  F: {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
};

const RELATIVE_UNITS: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] =
  [
    { unit: "year", seconds: 31_536_000 },
    { unit: "month", seconds: 2_592_000 },
    { unit: "day", seconds: 86_400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
  ];

export function formatRelativeTime(
  target: Date,
  now: Date,
  locale: string
): string {
  const diffSeconds = Math.round(
    (target.getTime() - now.getTime()) / MS_PER_SECOND
  );
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  for (const { unit, seconds } of RELATIVE_UNITS) {
    if (Math.abs(diffSeconds) >= seconds) {
      return formatter.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return formatter.format(diffSeconds, "second");
}

function formatPreview(
  code: StyleCode,
  date: Date,
  now: Date,
  locale: string
): string {
  if (code === "R") {
    return formatRelativeTime(date, now, locale);
  }
  return date.toLocaleString(locale, ABSOLUTE_STYLE_OPTIONS[code]);
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function toDatetimeLocalValue(date: Date): string {
  const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${datePart}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** English strings. A translated content file overrides these through `ui`. */
export const DISCORD_TIMESTAMP_GENERATOR_STRINGS = {
  shortTime: "Short time",
  longTime: "Long time",
  shortDate: "Short date",
  longDate: "Long date",
  shortDateTimeDefault: "Short date/time (default)",
  longDateTime: "Long date/time",
  relativeTime: "Relative time",
  dateAndTimeLabel: "Date and time (your local timezone)",
  unixEpoch: "one:Unix epoch: {epoch} second|other:Unix epoch: {epoch} seconds",
  allSevenDiscordStyles: "All seven Discord styles",
  style: "Style",
  code: "Code",
  discordShows: "Discord shows",
  copy: "Copy",
  copied: "Copied!",
  previewsUseLocale:
    "Previews use this page's language, so Discord's exact wording may differ slightly — the copied code is exact.",
} as const;

export default function DiscordTimestampGeneratorWidget() {
  const [value, setValue] = useState("");
  const [now, setNow] = useState(() => new Date());
  const [copiedCode, setCopiedCode] = useState<StyleCode | null>(null);

  const locale = useToolLocale();
  const strings = useToolUi(DISCORD_TIMESTAMP_GENERATOR_STRINGS);

  const styles: { code: StyleCode; label: string }[] = [
    { code: "t", label: strings.shortTime },
    { code: "T", label: strings.longTime },
    { code: "d", label: strings.shortDate },
    { code: "D", label: strings.longDate },
    { code: "f", label: strings.shortDateTimeDefault },
    { code: "F", label: strings.longDateTime },
    { code: "R", label: strings.relativeTime },
  ];

  useEffect(() => {
    setValue(toDatetimeLocalValue(new Date()));
    const interval = setInterval(
      () => setNow(new Date()),
      SECONDS_PER_MINUTE * MS_PER_SECOND
    );
    return () => clearInterval(interval);
  }, []);

  const parsed = value.length > 0 ? new Date(value) : null;
  const valid = parsed !== null && !Number.isNaN(parsed.getTime());
  const epoch = valid ? Math.floor(parsed.getTime() / MS_PER_SECOND) : 0;

  function handleCopy(code: StyleCode) {
    writeToClipboard(`<t:${epoch}:${code}>`).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  }

  return (
    <>
      {/* Date input */}
      <section className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="timestamp-input"
        >
          {strings.dateAndTimeLabel}
        </label>
        <input
          className="w-full max-w-sm rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id="timestamp-input"
          onChange={(e) => setValue(e.target.value)}
          type="datetime-local"
          value={value}
        />
        {valid && (
          <p className="text-muted-foreground text-xs">
            {/* tPlural keeps "{epoch}" as a slot so the number can stay monospaced. */}
            {renderSlots(
              tPlural(strings.unixEpoch, locale, epoch, { epoch: "{epoch}" }),
              { epoch: <span className="font-mono">{epoch}</span> }
            )}
          </p>
        )}
      </section>

      {/* Styles table */}
      {valid && parsed !== null && (
        <section className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.allSevenDiscordStyles}
          </p>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border border-b text-muted-foreground text-xs">
                  <th className="px-4 py-2 font-medium">{strings.style}</th>
                  <th className="px-4 py-2 font-medium">{strings.code}</th>
                  <th className="px-4 py-2 font-medium">
                    {strings.discordShows}
                  </th>
                  <th className="px-4 py-2 font-medium">
                    <span className="sr-only">{strings.copy}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {styles.map((style) => (
                  <tr key={style.code}>
                    <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">
                      {style.label}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-foreground text-xs">
                      {`<t:${epoch}:${style.code}>`}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-foreground">
                      {formatPreview(style.code, parsed, now, locale)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        className="rounded-md border border-border bg-card px-3 py-1 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                        onClick={() => handleCopy(style.code)}
                        type="button"
                      >
                        {copiedCode === style.code
                          ? strings.copied
                          : strings.copy}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted-foreground text-xs">
            {strings.previewsUseLocale}
          </p>
        </section>
      )}
    </>
  );
}
